/** Phones, the contact form open in the menu's panel (at the bottom of the
 *  screen): the keyboard. iOS, when a field near the bottom is tapped, pans
 *  the whole screen to show it, and back, which reads as the form jumping
 *  about. So here:
 *  - a tap on a field focuses it ourselves, without the page being scrolled
 *    to it (focus({ preventScroll })), so iOS has nothing to pan;
 *  - the panel rises clear of the keyboard itself, by the keyboard's height
 *    (--kb on the menu's root; contact.css moves .n27-wrap by it), eased as
 *    the keyboard is: up as the keyboard comes, from the tap (its height as
 *    last seen), and down as it goes, from the moment the field's left;
 *  - the visible screen, as the browser reports it (visualViewport), has the
 *    last word, and should iOS pan all the same, the panel follows it. */

// the keyboard's height, once seen (it's the same each time)
let seen = 0
const FIELD = ".n27-contact .ct-input, .n27-contact .ct-msg"

export function followKeyboard(root: HTMLElement) {
    const vv = window.visualViewport
    let kb = 0
    const set = (k: number) => {
        kb = k
        root.style.setProperty("--kb", k + "px")
        root.style.setProperty("--kb-top", Math.max(0, vv?.offsetTop || 0) + "px")
    }
    const actual = () => (vv ? Math.max(0, Math.round(innerHeight - vv.height)) : 0)
    const onViewport = () => {
        const a = actual()
        if (a > 60) seen = a
        set(a > 60 ? a : 0)
    }
    const field = (t: EventTarget | null) => (t as Element | null)?.closest?.(FIELD) || null
    const inputOf = (f: Element) => (f.matches(".ct-input") ? f : f.querySelector(".ct-input")) as HTMLInputElement | HTMLTextAreaElement | null

    // a tap on a field: focused here, not scrolled to; up with the keyboard
    const touchEnd = (e: TouchEvent) => {
        const f = field(e.target)
        const i = f && inputOf(f)
        if (!i) return
        e.preventDefault()
        if (document.activeElement !== i) i.focus({ preventScroll: true })
        try {
            i.setSelectionRange(i.value.length, i.value.length)
        } catch {}
        if (kb < 60 && seen) set(seen)
    }
    // leaving the fields (not for another of them): down with the keyboard
    const focusOut = (e: FocusEvent) => {
        if (!field(e.relatedTarget)) set(0)
    }
    document.addEventListener("touchend", touchEnd, { passive: false, capture: true })
    root.addEventListener("focusout", focusOut)
    vv?.addEventListener("resize", onViewport)
    vv?.addEventListener("scroll", onViewport)
    onViewport()

    return {
        /** the keyboard's up (or was, a moment ago: a tap on the blur may have
         *  taken the focus off the field already, the keyboard still going) */
        up: () => kb > 60 || actual() > 60,
        /** the keyboard away, the panel back down with it: done when it's there */
        dismiss: () =>
            new Promise<void>((done) => {
                ;(document.activeElement as HTMLElement | null)?.blur?.()
                set(0)
                setTimeout(done, 360)
            }),
        release: () => {
            document.removeEventListener("touchend", touchEnd, { capture: true })
            root.removeEventListener("focusout", focusOut)
            vv?.removeEventListener("resize", onViewport)
            vv?.removeEventListener("scroll", onViewport)
            root.style.removeProperty("--kb")
            root.style.removeProperty("--kb-top")
        },
    }
}
